import VueI18n, { TranslateResult, LocaleMessages } from 'vue-i18n';

export interface INotifier {
  error: (msg: string) => void;
  log: (msg: string) => void;
}

interface IRoute {
  name: string;
}

export interface IRouter {
  push: (route: IRoute) => void;
}

export interface ITranslator {
  t: (key: string, values?: VueI18n.Values) => TranslateResult;
  messages: LocaleMessages;
  locale: string;
}

export interface IConfig {
  notifier: INotifier;
  router: IRouter;
  translator: ITranslator;
}

interface ISMessage {
  printable?: string;
  errorKey?: string;
  errorKeyParams?: object;
}

interface IValidationError {
  model: string;
  attribute: string;
  type: string;
  value?: string;
  custom_model_name?: {
    t_key: string; 
    [key: string]: string;
  };
  options?: {[key: string]: string | number};
}
type ValidationErrors = ReadonlyArray<IValidationError>;

interface IErrorExtensions {
  smessage?: ISMessage;
  code?: string;
  validations?: ValidationErrors;
}

// based on
// import { GraphQLError } from 'graphql/error/GraphQLError';
interface IError {
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extensions?: any;
  sextensions?: IErrorExtensions;
}

type GQLErrors = ReadonlyArray<IError>;

interface INetworkErrorResponse {
  status: number;
}

interface INetworkError {
  response?: INetworkErrorResponse;
}

// based on
// import { ErrorResponse } from 'apollo-link-error';
export interface IErrors {
  graphQLErrors?: GQLErrors;
  networkError?: INetworkError | Error | null;
}

function isDevelopment() {
  return process.env.NODE_ENV === 'development';
}

function snakeToPascalCase(str: string): string {
  const strArr = str.toLowerCase().split('_');
  for (let i = 0; i < strArr.length; i++) {
    strArr[i] = strArr[i].charAt(0).toUpperCase() + strArr[i].slice(1);
  }
  return strArr.join('');
}

function snakeToCamelCase(str: string): string {
  const strArr = snakeToPascalCase(str).split('');
  strArr[0] = strArr[0].toLowerCase();
  return strArr.join('');
}

export function hasCode(errors: IErrors, code: string): boolean {
  code = code.toUpperCase();

  if (!errors) return false;
  if (!errors.graphQLErrors) return false;

  return errors.graphQLErrors.some(error =>
    error.sextensions &&
    error.sextensions.code &&
    error.sextensions.code.toUpperCase() === code,
  );
}

export function createErrorHandler(config: IConfig) {
  const { translator, notifier, router } = config;

  function lookupErrorKey(key: string, params?: object): string {
    return translator.t(`GQL.ValidationType.${key}`, params) as string;
  }

  function notifyFallbackError(){
    notifier.error(lookupErrorKey("failed"));
  }

  function findValidationType(
    model: string, attribute: string, type: string,
    options?: { [key: string]: string | number },
  ): string {
    // `equal_to` is used for multiple types, in case of date
    // the translation option is put in `.date` instead of `.count`
    if (type === 'equal_to' && options?.date) {
      options.count = options.date;
      delete options.date;
    }

    const byAttribute = `GQL.ValidationType.${model}.${snakeToPascalCase(attribute)}.${type}`;
    if (translator.t(byAttribute, options) !== byAttribute) {
      return translator.t(byAttribute, options) as string;
    }

    const byModel = `GQL.ValidationType.${model}.${type}`;
    if (translator.t(byModel, options) !== byModel) {
      return translator.t(byModel, options) as string;
    }

    return translator.t(`GQL.ValidationType.${type}`, options) as string;
  }

  function renderValidationError(error: IValidationError): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const GQL = translator.messages[translator.locale].GQL as any;

    let model = error.model;
    let attribute = snakeToPascalCase(error.attribute);
    let type = error.type;

    if (
      GQL.CustomMessage[model] &&
      GQL.CustomMessage[model][attribute] &&
      GQL.CustomMessage[model][attribute][type]
    ) {
      return GQL.CustomMessage[model][attribute][type];
    } else {
      if (error.custom_model_name) {
        const customModelName = error.custom_model_name;
        model = translator.t(
          `GQL.Model.${error.model}.${snakeToCamelCase(customModelName.t_key)}`, 
          customModelName,
        ) as string; 
      } else {
        model = translator.t(`GQL.Model.${error.model}.name`) as string;
      }
      if(error.attribute === 'base') {
        attribute = '';
      } else {
        attribute = translator
          .t(`GQL.Model.${error.model}.Attribute.${snakeToCamelCase(error.attribute)}`, error.options) as string;
      }
      type = findValidationType(error.model, error.attribute, error.type, error.options);

      return translator.t(`GQL.message`, { model, attribute, type }) as string;
    }
  }

  function renderValidationErrors(errors: ValidationErrors): string[] {
    // Works similar to generate_message
    // https://api.rubyonrails.org/classes/ActiveModel/Errors.html#method-i-generate_message
    // Priorities:
    // GQL.ValidationType.MODEL.ATTRIBUTE(toPascal).MESSAGE
    // GQL.ValidationType.MODEL.MESSAGE
    // GQL.ValidationType.MESSAGE

    // Example
    // GQL.ValidationType.User.Title.blank
    // GQL.ValidationType.User.blank
    // GQL.ValidationType.blank

    return errors.map(error => {
      return renderValidationError(error);
    });
  }

  function handleErrorExtensions(errors: GQLErrors) {
    if (!errors.length) {
      return;
    }

    errors.forEach(error => {
      if (isDevelopment()) {
        // according to the graphql spec, the top-level message
        // is intended for developers, so that's what we're using it as
        // the server may also, for security, hide information from this field
        // when it's not running in development
        notifier.error(`DEVONLY: ${error.message}`);
        notifier.log(`DEVONLY: ${error.message}`);
      }

      const validationExt = error.sextensions?.validations;
      if (validationExt) {
        if (!validationExt.length) {
          notifyFallbackError();
        }
        const errors = renderValidationErrors(validationExt);
        errors.forEach(err => {
          notifier.error(err);
        });
      }

      const messageExt = error.sextensions?.smessage;
      if (messageExt) {
        if (messageExt.printable) {
          notifier.error(messageExt.printable);
        } else if (messageExt.errorKey) {
          const toShow = lookupErrorKey(messageExt.errorKey, messageExt.errorKeyParams);
          notifier.error(toShow);
        } else {
          notifyFallbackError();
          if (isDevelopment()) {
            const ohGod = `misconfigured message extension for error: ${error}`;
            notifier.error(ohGod);
            notifier.log(ohGod);
          }
        }
      }

      if (!error.sextensions) {
        notifyFallbackError();
      }
    });
  }

  function handleErrors(errors: IErrors): void {
    if (!errors) return;

    const networkError = errors.networkError;
    if (networkError) {
      if (
        (networkError as INetworkError).response?.status === 401
      ) {
        router.push({ name: 'Login' });
      } else {
        if (isDevelopment()) {
          notifier.error(`DEVONLY networkError: ${networkError}`);
          notifier.log(`DEVONLY networkError: ${networkError}`);
        } else {
          notifier.error(translator.t('networkError') as string);
        }
      }
    } else {
      const graphQLErrors = errors.graphQLErrors;

      if (hasCode(errors, 'NOT_FOUND')) {
        router.push({ name: 'NotFound' });
      }

      if (graphQLErrors) {
        handleErrorExtensions(graphQLErrors);
      }
    }
  }

  return {
    handle: handleErrors,
  };
}
